/**
 * Build Info Service
 *
 * Collects everything needed to identify exactly what build is running:
 * native app version, active OTA bundle, build-time git stamp, and the
 * environment config. Reuses plugins already in the app — no new deps.
 *
 * Mirrors the defensive style of otaUpdateService: never throws, and degrades
 * gracefully on web (where native plugins are no-ops) or offline (where the
 * Firestore OTA lookup is skipped).
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { doc, getDocFromServer, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { OTA_DOC_PATH, OTA_DOC_ID, withTimeout } from './otaUpdateService';

const OTA_LOOKUP_TIMEOUT_MS = 5000;

export interface BuildInfo {
  // App (native only)
  appName?: string;
  appId?: string; // e.g. com.lens.dsa.dev
  versionName?: string; // e.g. 1.0.17-DEV  (App.getInfo().version)
  versionCode?: string; // e.g. 17          (App.getInfo().build)
  platform: string; // android / ios / web
  isNative: boolean;

  // OTA
  currentBundleId?: string; // undefined => running the built-in APK bundle
  latestBundleId?: string; // from appConfig/otaConfig
  otaUpdatedAt?: string; // ISO string of last OTA deploy
  otaPending: boolean; // a newer bundle is available

  // Build-time stamp (injected via Vite define)
  gitSha: string;
  gitBranch: string;
  buildTime: string;
  appEnv: string; // vite mode

  // Env config (VITE_*)
  environment?: string;
  firebaseProject?: string;
  chatbotUrl?: string;
  logLevel?: string;
}

/** Normalize a Firestore timestamp / string / date into an ISO string. */
function toIso(value: unknown): string | undefined {
  if (!value) return undefined;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  return undefined;
}

/**
 * Gather build info. Always resolves — failures in any one source are swallowed
 * so the rest of the report still renders.
 */
export async function getBuildInfo(): Promise<BuildInfo> {
  const isNative = Capacitor.isNativePlatform();

  const info: BuildInfo = {
    platform: Capacitor.getPlatform(),
    isNative,
    otaPending: false,
    gitSha: __GIT_SHA__,
    gitBranch: __GIT_BRANCH__,
    buildTime: __BUILD_TIME__,
    appEnv: __APP_ENV__,
    environment: import.meta.env.VITE_ENVIRONMENT,
    firebaseProject: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    chatbotUrl: import.meta.env.VITE_CHATBOT_URL,
    logLevel: import.meta.env.VITE_LOG_LEVEL,
  };

  // Native app metadata
  if (isNative) {
    try {
      const appInfo = await App.getInfo();
      info.appName = appInfo.name;
      info.appId = appInfo.id;
      info.versionName = appInfo.version;
      info.versionCode = appInfo.build;
    } catch (error) {
      console.error('[BuildInfo] Failed to read App.getInfo:', error);
    }

    try {
      const { bundleId } = await LiveUpdate.getCurrentBundle();
      info.currentBundleId = bundleId ?? undefined;
    } catch (error) {
      console.error('[BuildInfo] Failed to read current OTA bundle:', error);
    }
  }

  // Latest OTA config from Firestore (best-effort, timed out so offline is fine)
  try {
    const docRef = doc(db, OTA_DOC_PATH, OTA_DOC_ID);
    const snapshot = await withTimeout(getDocFromServer(docRef), OTA_LOOKUP_TIMEOUT_MS);
    if (snapshot?.exists()) {
      const data = snapshot.data() as { bundleId?: string; updatedAt?: unknown };
      info.latestBundleId = data.bundleId;
      info.otaUpdatedAt = toIso(data.updatedAt);
      info.otaPending = Boolean(
        data.bundleId && data.bundleId !== info.currentBundleId,
      );
    }
  } catch (error) {
    console.error('[BuildInfo] Failed to read OTA config:', error);
  }

  return info;
}
